import { EnvironmentProviders, inject, provideAppInitializer, Provider } from '@angular/core';
import { ROUTES } from '@angular/router';

import {
  APP_CONFIG_TOKEN,
  APP_KEYS,
  AppKey,
  CanvasCommunicationService,
  DevicesErrorHandlerService,
  ENVIRONMENT_TOKEN,
  MERGE_FILES_SERVICE,
  PaletteService,
  ReadingsService,
} from '@olympus/ngx-shared';
import { UNIS_UT_AEROVIEW_APP_NAME, UNIS_UT_ONEUTVIEW_APP_NAME } from 'electron-app/app-names';
import { DPU_SERVICE } from 'shared-business-angular/dpu-file';
import { provideTilingCanvas } from 'shared-business-angular/tiling-canvas';
import { COLOR_PALETTE_SERVICE } from 'ut-business/models';
import { AppConfig } from 'ut-business/shared';

import { environment } from '../environments/environment';
import { aeroviewConfig, oneutviewConfig, windviewConfig } from '../features-config';
import { msalCanActivateGuard } from './core/guards/msal.guard';
import { APP_AUTHENTICATION_SERVICE_TOKEN, AuthenticationService } from './core/services/authentication/authentication.interface';
import { EvcAuthenticationService } from './core/services/authentication/EvCAuthentication.service';
import { MergeFilesService } from './core/services/merge-files/merge-files.service';
import { provideFeaturesWithLicensing } from './modules/license/services/provide-licensing';

function getConfigKey(): AppKey {
  if (environment.electron) {
    if (window.electron.appName === UNIS_UT_ONEUTVIEW_APP_NAME) {
      return APP_KEYS.ONEUTVIEW;
    }

    return window.electron.appName === UNIS_UT_AEROVIEW_APP_NAME ? APP_KEYS.AEROVIEW : APP_KEYS.WINDVIEW;
  } else {
    if (environment.APP_NAME === APP_KEYS.ONEUTVIEW) {
      return APP_KEYS.ONEUTVIEW;
    }

    return environment.APP_NAME?.includes('aero') ? APP_KEYS.AEROVIEW : APP_KEYS.WINDVIEW;
  }
}

const APP_CONFIGS: Record<AppKey, AppConfig> = {
  [APP_KEYS.AEROVIEW]: aeroviewConfig,
  [APP_KEYS.WINDVIEW]: windviewConfig,
  [APP_KEYS.ONEUTVIEW]: oneutviewConfig,
};

function initializeIcons(): void {
  const configKey = getConfigKey();

  const favicon: HTMLElement | null = document.getElementById('favicon');
  const appleTouchIcon: HTMLElement | null = document.getElementById('apple-touch-icon');
  const appleTouchIconPrecomp: HTMLElement | null = document.getElementById('apple-touch-icon-precomposed');

  if (favicon) {
    (favicon as HTMLLinkElement).href = `./assets/${configKey}-icon/favicon.png`;
  }

  if (appleTouchIcon) {
    (appleTouchIcon as HTMLLinkElement).href = `./assets/${configKey}-icon/favicon.png`;
  }

  if (appleTouchIconPrecomp) {
    (appleTouchIconPrecomp as HTMLLinkElement).href = `./assets/${configKey}-icon/favicon.png`;
  }
}

export const appInitializerConfig: (Provider | EnvironmentProviders)[] = [
  // in this group because depending on `initializeIcons`
  { provide: APP_CONFIG_TOKEN, useValue: APP_CONFIGS[getConfigKey()] },
  { provide: ENVIRONMENT_TOKEN, useValue: environment },
  { provide: APP_AUTHENTICATION_SERVICE_TOKEN, useClass: EvcAuthenticationService },
  provideFeaturesWithLicensing(),
  provideAppInitializer(() => initializeIcons()),

  provideAppInitializer(() => {
    const readingsService: ReadingsService = inject(ReadingsService);

    return readingsService.initialize();
  }),
  provideAppInitializer(() => {
    const deviceToast: DevicesErrorHandlerService = inject(DevicesErrorHandlerService);

    return deviceToast.initialize();
  }),
  provideAppInitializer(() => {
    if (environment.electron) {
      return Promise.resolve();
    }

    const authenticationService: AuthenticationService = inject(APP_AUTHENTICATION_SERVICE_TOKEN);

    return authenticationService.initialize();
  }),
  {
    provide: MERGE_FILES_SERVICE,
    useExisting: MergeFilesService,
  },
  provideTilingCanvas({
    communication: {
      provide: DPU_SERVICE,
      useFactory: (communicationService: CanvasCommunicationService) => communicationService.communication,
      deps: [CanvasCommunicationService],
    },
    palettes: {
      provide: COLOR_PALETTE_SERVICE,
      useFactory: (service: PaletteService) => service,
      deps: [PaletteService],
    },
    guardFn: msalCanActivateGuard,
  }),
  // Fallback must be registered after all routes to prevent it from catching all navigation attempts prematurely.
  {
    provide: ROUTES,
    multi: true,
    useValue: [{ path: '**', redirectTo: '' }],
  },
];
